import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from 'react-table';
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from '../../components/InputComponent';
import Layout from "../../components/Layout/Layout";
import TableComponent from '../../components/TableComponent';
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from '../../helpers/Utilities';
import { CounterTypeDTO } from '../../models/Counter/CounterTypeDTO';
import { Parameter } from '../../models/Parameter';
import { CounterTypeService } from "../../services/CounterTypeService";

export default function CounterTypeIndex() {
  // Hooks
  const navigate = useNavigate();
  const intl = useIntl();
  const counterService = new CounterTypeService();

  const [name, setName] = useState<string>(
    Utilities.LoadParameterFromURLQuery("name", "string", ""),
  );
  const [code, setCode] = useState<string>(
    Utilities.LoadParameterFromURLQuery("code", "string", ""),
  );
  const [counterTypes, setCounterTypes] = useState<CounterTypeDTO[]>([]);
  const [initialSortBy] = useState<SortingRule<object>[]>(
    Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"),
  );
  const pageSizes: number[] = [5, 15, 30];
  const [goToPage, setGoToPage] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
  const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));
  const [currentPage, setCurrentPage] = useState<number>();
  const [currentPageSize, setCurrentPageSize] = useState<number>();
  const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
  const [reloadSearch, setReloadSearch] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="Screens.CounterTypes.Table.Code" />,
        accessor: "code",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.CounterTypes.Table.Name" />,
        accessor: "name",
        width: 35,
      },
      {
        Header: <FormattedMessage id="Screens.CounterTypes.Table.Unit" />,
        accessor: "unit",
        width: 15,
        Cell: ({ value }: any) => <span>{value ? value : "-"}</span>,
      },
      {
        Header: <FormattedMessage id="Screens.CounterTypes.Table.Description" />,
        accessor: "description",
        width: 35,
        Cell: ({ value }: any) => <span>{value ? value : "-"}</span>,
      }
    ],
    [],
  );

  const mountSearchParameters = (): Parameter[] => {
    var parameters: Parameter[] = [];

    parameters.push(new Parameter("name", name));
    parameters.push(new Parameter("code", code));
    return parameters;
  };

  const fetchCounterTypes = async (
    page: number,
    pageSize: number,
    sortBy: SortingRule<object>[] | null,
  ) => {

    //Atualizamos na history do browser a current page e pageSize
    var searchUrl: string =
      `?page=${page}&pageSize=${pageSize}${Utilities.AddParamToQueryIfValue(
        "name",
        "string",
        name,
      )}${Utilities.AddParamToQueryIfValue(
        "code",
        "string",
        code,
      )}`;

    if (sortBy != null && sortBy.length > 0) {
      searchUrl += `${Utilities.AddParamToQueryIfValue(
        "sortField",
        "string",
        sortBy[0].id,
      )}${Utilities.AddParamToQueryIfValue(
        "sortOrder",
        "string",
        sortBy[0].desc === true ? "DESC" : "ASC",
      )}`;
    }

    //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota
    if (searchParams.toString() === "") {
      navigate(`/counterTypes${searchUrl}`, { replace: true });
    } else {
      //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
      navigate(`/counterTypes${searchUrl}`);
    }

    setLoading(true);

    //Vamos obter os dados
    var response = await counterService.GetAll(
      mountSearchParameters(),
      Utilities.MountSortParameters(sortBy),
      page,
      pageSize,
    );

    if (response.success !== true) {
      setCounterTypes([]);
      setPageCount(0);
      setLoading(false);
      setCurrentPageSize(0);
      setCurrentPage(0);
      setCurrentSorting(null);
      Toast.Show("error", response.message);
      return;
    }

    //Atribuímos os dados
    setCounterTypes(response.items);
    setGoToPage(0);
    setPageCount(response.totalPages);
    setLoading(false);
    setCurrentPageSize(pageSize);
    setCurrentPage(page);
    setCurrentSorting(sortBy);

    //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
    if (response.totalPages > 0 && page > response.totalPages) {
      setGoToPage(1);
    }
  };

  const onNewCounterTypeClick = () => {
    navigate(Routes.counterTypes.create);
  };

  const onEnter = () => {
    setCurrentPage(1);
    setGoToPage(1);
    fetchCounterTypes(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };

  const onButtonClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    fetchCounterTypes(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };

  const onRemoveFiltersClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    setName("");
    setCode("");
    setReloadSearch(true);
  };

  useEffect(() => {
    if (reloadSearch === true) {
      fetchCounterTypes(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
      setReloadSearch(false);
    }
    return () => { };
  }, [reloadSearch]);

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <span className="pageTitle">
            <FormattedMessage id="Screens.CounterTypes.Title" />
          </span>
        </div>

        <Row>
          <Col
            xl={2}
            lg={3}
            md={3}
            sm={12}
            xs={12}
            style={{ marginTop: "1em" }}
          >
            <ButtonComponent
              text={intl.formatMessage({ id: "Screens.CounterTypes.Button.CreateCounterType" })}
              onClick={onNewCounterTypeClick}
            />
          </Col>
        </Row>

        <div className="pageBody">
          <>
            <Row>
              <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                style={{ width: "100%" }}
                type={"text"}
                onEnter={onEnter}
                value={code}
                label={intl.formatMessage({ id: "Screens.CounterTypes.SearchField1" })}
                readOnly={false}
                onChange={(t: string) => setCode(t)}
              />
              <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                style={{ width: "100%" }}
                type={"text"}
                onEnter={onEnter}
                value={name}
                label={intl.formatMessage({ id: "Screens.CounterTypes.SearchField2" })}
                readOnly={false}
                onChange={(t: string) => setName(t)}
              />

            </Row>

            <Row>
              <Col xl={6} lg={6} md={6} sm={12} xs={12}
                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
              >
                <ButtonComponent
                  text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                  onClick={onButtonClick}
                />
                <ButtonComponent
                  text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                  onClick={onRemoveFiltersClick}
                />
              </Col>
            </Row>

            <div className="pageComponent" style={{ width: "100%" }}>
              <TableComponent
                goToPage={goToPage}
                loading={loading}
                pageCount={pageCount}
                columns={columns}
                manualPagination={true}
                fetchData={fetchCounterTypes}
                pageSizes={pageSizes}
                initialPage={page}
                initialPageSize={pageSize}
                initialSortBy={initialSortBy}
                data={counterTypes}
                dontOrderFirstCollumn={true}
              />
            </div>
          </>
        </div>
      </div>
    </Layout>
  );
}
